import { useAuth } from 'auth';
import AppStoreContext from 'contexts/AppStoreContext';
import { OrderedSet } from 'immutable';
import * as React from 'react';
import { License } from 'records/License';
import { Roles } from 'records/Role';
import { User } from 'records/User';

export type UserInfoValue = {
  getCurrentUserRole: () => Roles;
  getUserFirstAndLastName: () => { firstname: string; lastname: string };
  getCurrentUser: () => User;
  getAllUserEmails: () => OrderedSet<string>;
  getUserByEmail: (email: string) => User;
  getLicense: () => License;
  isLicensedToCreateProjects: () => boolean;
};

type UserInfoConsumerProps = {
  children: (userInfo: UserInfoValue) => React.ReactNode;
};

export type WithUserInfoProps = {
  userInfo: UserInfoValue;
};

const UserInfoContext = React.createContext<UserInfoValue | null>(null);

export const UserInfoProvider = UserInfoContext.Provider;

export const AppUserInfoProvider: React.FC = ({ children }) => {
  const { userId } = useAuth();
  const { state: appState } = React.useContext(AppStoreContext);

  const userInfo: UserInfoValue = {
    getCurrentUserRole: () => appState.getUserRole(userId),
    getUserFirstAndLastName: () => ({
      firstname: appState.eula.firstname,
      lastname: appState.eula.lastname,
    }),
    getCurrentUser: () =>
      appState.users.find((u) => userId === u.id) || new User(),
    getAllUserEmails: () =>
      appState.users
        .map((u) => u.email)
        .toOrderedSet()
        .sort(),
    getUserByEmail: (email: string) =>
      appState.users.find((u) => email === u.email) || new User(),
    getLicense: () => appState.userLicense,
    isLicensedToCreateProjects: () =>
      new Set(['Core', 'Complete']).has(appState.userLicense.package),
  };

  return (
    <UserInfoContext.Provider value={userInfo}>
      {children}
    </UserInfoContext.Provider>
  );
};

export const useUserInfo = (): UserInfoValue => {
  const userInfo = React.useContext(UserInfoContext);

  if (userInfo === null) {
    throw new Error('useUserInfo must be used within a UserInfoProvider');
  }

  return userInfo;
};

export const UserInfoConsumer: React.FC<UserInfoConsumerProps> = ({
  children,
}) => {
  return (
    <UserInfoContext.Consumer>
      {(userInfo) => {
        if (userInfo === null) {
          throw new Error(
            'UserInfoConsumer must be used within a UserInfoProvider'
          );
        }
        return children(userInfo);
      }}
    </UserInfoContext.Consumer>
  );
};

export function withUserInfo<P>(
  WrappedComponent: React.ComponentType<P & WithUserInfoProps>
): React.FC<P> {
  const WithUserInfoWrapper: React.FC<P> = (props) => {
    return (
      <UserInfoConsumer>
        {(userInfo) => (
          // @ts-ignore - issue with @emotion/react https://github.com/emotion-js/emotion/issues/2169
          <WrappedComponent {...props} userInfo={userInfo} />
        )}
      </UserInfoConsumer>
    );
  };

  WithUserInfoWrapper.displayName = `withUserInfo(${
    WrappedComponent.displayName || WrappedComponent.name
  })`;

  return WithUserInfoWrapper;
}
